// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions, type UseMutationOptions } from '@tanstack/vue-query';

import { client } from '../client.gen';
import { acknowledgeDisclaimer, callback, check, externalUser, forgotPassword, generateApiKey, impersonate, login, logout, migrateUser, type Options, providers, renew, requestEmailVerification, resetPassword, verifyEmail } from '../sdk.gen';
import type { AcknowledgeDisclaimerData, AcknowledgeDisclaimerError2, AcknowledgeDisclaimerResponse2, CallbackData, CallbackResponse, CheckData, CheckError2, CheckResponse2, ExternalUserData, ExternalUserError2, ExternalUserResponse2, ForgotPasswordData, ForgotPasswordError2, ForgotPasswordResponse2, GenerateApiKeyData, GenerateApiKeyError2, GenerateApiKeyResponse2, ImpersonateData, ImpersonateError2, ImpersonateResponse2, LoginData, LoginError2, LoginResponse, LogoutData, LogoutError2, LogoutResponse2, MigrateUserData, MigrateUserError2, MigrateUserResponse2, ProvidersData, ProvidersError2, ProvidersResponse2, RenewData, RenewError2, RenewResponse2, RequestEmailVerificationData, RequestEmailVerificationError2, RequestEmailVerificationResponse2, ResetPasswordData, ResetPasswordError2, ResetPasswordResponse2, VerifyEmailData, VerifyEmailError2, VerifyEmailResponse2 } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseUrl: options?.baseUrl || (options?.client ?? client).getConfig().baseUrl } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [params];
};

export const checkQueryKey = (options?: Options<CheckData>) => createQueryKey('check', options);

/**
 * Check if a login token is valid
 */
export const checkOptions = (options?: Options<CheckData>) => queryOptions<CheckResponse2, CheckError2, CheckResponse2, ReturnType<typeof checkQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await check({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: checkQueryKey(options)
});

export const providersQueryKey = (options: Options<ProvidersData>) => createQueryKey('providers', options);

/**
 * List all the available providers
 */
export const providersOptions = (options: Options<ProvidersData>) => queryOptions<ProvidersResponse2, ProvidersError2, ProvidersResponse2, ReturnType<typeof providersQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await providers({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: providersQueryKey(options)
});

/**
 * Login in to IsardVDI
 */
export const loginMutation = (options?: Partial<Options<LoginData>>): UseMutationOptions<LoginResponse, LoginError2, Options<LoginData>> => {
    const mutationOptions: UseMutationOptions<LoginResponse, LoginError2, Options<LoginData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await login({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Create a isard user with an external identity provider
 */
export const externalUserMutation = (options?: Partial<Options<ExternalUserData>>): UseMutationOptions<ExternalUserResponse2, ExternalUserError2, Options<ExternalUserData>> => {
    const mutationOptions: UseMutationOptions<ExternalUserResponse2, ExternalUserError2, Options<ExternalUserData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await externalUser({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const callbackQueryKey = (options: Options<CallbackData>) => createQueryKey('callback', options);

/**
 * This method is used as a callback for external identity providers
 */
export const callbackOptions = (options: Options<CallbackData>) => queryOptions<CallbackResponse, DefaultError, CallbackResponse, ReturnType<typeof callbackQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await callback({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: callbackQueryKey(options)
});

/**
 * Renew the user session and obtain a new valid JWT
 */
export const renewMutation = (options?: Partial<Options<RenewData>>): UseMutationOptions<RenewResponse2, RenewError2, Options<RenewData>> => {
    const mutationOptions: UseMutationOptions<RenewResponse2, RenewError2, Options<RenewData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await renew({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Close the session
 */
export const logoutMutation = (options?: Partial<Options<LogoutData>>): UseMutationOptions<LogoutResponse2, LogoutError2, Options<LogoutData>> => {
    const mutationOptions: UseMutationOptions<LogoutResponse2, LogoutError2, Options<LogoutData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await logout({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Acknowledge the IsardVDI disclaimer
 */
export const acknowledgeDisclaimerMutation = (options?: Partial<Options<AcknowledgeDisclaimerData>>): UseMutationOptions<AcknowledgeDisclaimerResponse2, AcknowledgeDisclaimerError2, Options<AcknowledgeDisclaimerData>> => {
    const mutationOptions: UseMutationOptions<AcknowledgeDisclaimerResponse2, AcknowledgeDisclaimerError2, Options<AcknowledgeDisclaimerData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await acknowledgeDisclaimer({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Request an email verification
 */
export const requestEmailVerificationMutation = (options?: Partial<Options<RequestEmailVerificationData>>): UseMutationOptions<RequestEmailVerificationResponse2, RequestEmailVerificationError2, Options<RequestEmailVerificationData>> => {
    const mutationOptions: UseMutationOptions<RequestEmailVerificationResponse2, RequestEmailVerificationError2, Options<RequestEmailVerificationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await requestEmailVerification({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Verify the user email
 */
export const verifyEmailMutation = (options?: Partial<Options<VerifyEmailData>>): UseMutationOptions<VerifyEmailResponse2, VerifyEmailError2, Options<VerifyEmailData>> => {
    const mutationOptions: UseMutationOptions<VerifyEmailResponse2, VerifyEmailError2, Options<VerifyEmailData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await verifyEmail({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Request a password reset
 */
export const forgotPasswordMutation = (options?: Partial<Options<ForgotPasswordData>>): UseMutationOptions<ForgotPasswordResponse2, ForgotPasswordError2, Options<ForgotPasswordData>> => {
    const mutationOptions: UseMutationOptions<ForgotPasswordResponse2, ForgotPasswordError2, Options<ForgotPasswordData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await forgotPassword({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Reset the user password
 */
export const resetPasswordMutation = (options?: Partial<Options<ResetPasswordData>>): UseMutationOptions<ResetPasswordResponse2, ResetPasswordError2, Options<ResetPasswordData>> => {
    const mutationOptions: UseMutationOptions<ResetPasswordResponse2, ResetPasswordError2, Options<ResetPasswordData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await resetPassword({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Request a token for user migration
 *
 * Internal call from isard-api only. Users cannot call this endpoint
 */
export const migrateUserMutation = (options?: Partial<Options<MigrateUserData>>): UseMutationOptions<MigrateUserResponse2, MigrateUserError2, Options<MigrateUserData>> => {
    const mutationOptions: UseMutationOptions<MigrateUserResponse2, MigrateUserError2, Options<MigrateUserData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await migrateUser({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Generate a API key for a specific user
 *
 * An API key will be created for the user with a durability of 1 year
 */
export const generateApiKeyMutation = (options?: Partial<Options<GenerateApiKeyData>>): UseMutationOptions<GenerateApiKeyResponse2, GenerateApiKeyError2, Options<GenerateApiKeyData>> => {
    const mutationOptions: UseMutationOptions<GenerateApiKeyResponse2, GenerateApiKeyError2, Options<GenerateApiKeyData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await generateApiKey({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Generate a token for a specific user
 *
 * Only admins, managers or external apps can call this endpoint
 */
export const impersonateMutation = (options?: Partial<Options<ImpersonateData>>): UseMutationOptions<ImpersonateResponse2, ImpersonateError2, Options<ImpersonateData>> => {
    const mutationOptions: UseMutationOptions<ImpersonateResponse2, ImpersonateError2, Options<ImpersonateData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await impersonate({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};
